import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { useDocumentRevisionFamilyCascadeTargets } from 'features/knowledgeDocumentViewer/hooks/useDocumentRevisionFamilyCascadeTargets'
import type {
  DocumentMetadataUserEditEntry,
  DocumentPageMetadataUserEditEntry,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { parseDocumentMetadataUserEditEntries } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { parseSystemDocumentEntries } from 'features/knowledgeDocumentViewer/types/systemDocumentEntrySchemas'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import {
  documentMetadataUserEditsByEntryIds,
  systemDocumentsByDrawingNumber,
} from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import {
  applyDocumentUserEditsToRevisionFamilyMembers,
  buildDocumentRevisionFamilyMembers,
  type DocumentRevisionFamilyMember,
  effectiveDocumentRevisionFamilyKey,
  filterDocumentsInRevisionFamily,
  filterMembersByEffectiveDocumentRevisionFamilyKey,
} from 'features/knowledgeDocumentViewer/utils/documentRevisionFamily'
import { documentMetadataUserEditEntryId } from 'features/knowledgeDocumentViewer/utils/metadataUserEditIds'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useCallback, useMemo } from 'react'

export type UseDocumentRevisionFamilyArgs = {
  document: SystemDocumentEntry | undefined
  documentUserEdit: DocumentMetadataUserEditEntry | undefined
  currentBqDocumentId: string | undefined
  /** Page number for counterpart cascade targets on family save. */
  cascadePageNum?: number
  enabled?: boolean
}

export type UseDocumentRevisionFamilyResult = {
  members: DocumentRevisionFamilyMember[]
  priorEditsByDocumentEntryId: ReadonlyMap<
    string,
    DocumentMetadataUserEditEntry
  >
  pagesByBqDocumentId: ReadonlyMap<string, SystemDocumentPageEntry>
  priorPageEditsByBqDocumentId: ReadonlyMap<
    string,
    DocumentPageMetadataUserEditEntry
  >
  mutatePriorEdits: () => Promise<unknown>
  arePriorEditsReady: boolean
  isLoading: boolean
  familySize: number
  hasRevisionFamily: boolean
}

export function useDocumentRevisionFamily({
  document,
  documentUserEdit,
  currentBqDocumentId,
  cascadePageNum,
  enabled = true,
}: UseDocumentRevisionFamilyArgs): UseDocumentRevisionFamilyResult {
  const { siteID } = useKnowledgeDocumentDataContext()
  const { getPipelineConfigScopedTags, isPipelineConfigScopeLoading } =
    usePipelineConfig()

  const systemDrawingNumber = document?.entry.data.drawing_number

  const loadOptions = useMemo(() => {
    if (
      !enabled ||
      isPipelineConfigScopeLoading ||
      !systemDrawingNumber?.trim()
    ) {
      return undefined
    }
    const scopedTags = getPipelineConfigScopedTags({ site: siteID })
    if (!scopedTags) return undefined
    return systemDocumentsByDrawingNumber(systemDrawingNumber, scopedTags)
  }, [
    enabled,
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    siteID,
    systemDrawingNumber,
  ])

  const { data: rawDocuments, isLoading: isLoadingDocuments } =
    useGetRolodexEntriesByLoadOptions<unknown>(loadOptions)

  const familyDocuments = useMemo(() => {
    if (rawDocuments === undefined) return []
    const parsed = parseSystemDocumentEntries(rawDocuments)
    return filterDocumentsInRevisionFamily(parsed, systemDrawingNumber)
  }, [rawDocuments, systemDrawingNumber])

  const members = useMemo(() => {
    if (familyDocuments.length === 0) return []
    return buildDocumentRevisionFamilyMembers({
      documents: familyDocuments,
      currentBqDocumentId,
    })
  }, [currentBqDocumentId, familyDocuments])

  const priorEditLoadOptions = useMemo(() => {
    if (!enabled || !siteID || members.length === 0) return undefined
    const entryIds = members.map((member) =>
      documentMetadataUserEditEntryId(siteID, member.bqDocumentId)
    )
    return documentMetadataUserEditsByEntryIds(entryIds, { site: siteID })
  }, [enabled, members, siteID])

  const {
    data: rawPriorEdits,
    isLoading: isLoadingPriorEdits,
    mutate: mutatePriorEdits,
  } = useGetRolodexEntriesByLoadOptions<unknown>(priorEditLoadOptions)

  const priorEditsByDocumentEntryId = useMemo(() => {
    const map = new Map<string, DocumentMetadataUserEditEntry>()
    if (rawPriorEdits === undefined || !siteID) return map
    const edits = parseDocumentMetadataUserEditEntries(rawPriorEdits)
    const editsById = new Map(edits.map((edit) => [edit.entry.id, edit]))
    for (const member of members) {
      const entryId = documentMetadataUserEditEntryId(
        siteID,
        member.bqDocumentId
      )
      const edit = editsById.get(entryId)
      if (edit) map.set(member.memberEntryId, edit)
    }
    return map
  }, [members, rawPriorEdits, siteID])

  const {
    pagesByBqDocumentId,
    priorPageEditsByBqDocumentId,
    mutateCascadePriorEdits,
    areCascadePriorEditsReady,
    isCascadeLoading,
  } = useDocumentRevisionFamilyCascadeTargets({
    members,
    cascadePageNum,
    enabled,
  })

  const editValuesByDocumentEntryId = useMemo(() => {
    const map = new Map<
      string,
      DocumentMetadataUserEditEntry['entry']['data']['values'] | undefined
    >()
    for (const member of members) {
      if (member.isCurrent && documentUserEdit !== undefined) {
        map.set(member.memberEntryId, documentUserEdit.entry.data.values)
        continue
      }
      map.set(
        member.memberEntryId,
        priorEditsByDocumentEntryId.get(member.memberEntryId)?.entry.data.values
      )
    }
    return map
  }, [documentUserEdit, members, priorEditsByDocumentEntryId])

  const currentFamilyKey = useMemo(
    () =>
      effectiveDocumentRevisionFamilyKey(
        document,
        documentUserEdit?.entry.data.values
      ),
    [document, documentUserEdit]
  )

  const displayMembers = useMemo(() => {
    const effectiveMembers = filterMembersByEffectiveDocumentRevisionFamilyKey({
      members,
      currentFamilyKey,
      editValuesByDocumentEntryId,
    })
    return applyDocumentUserEditsToRevisionFamilyMembers({
      members: effectiveMembers,
      editsByDocumentEntryId: editValuesByDocumentEntryId,
    })
  }, [currentFamilyKey, editValuesByDocumentEntryId, members])

  const mutateAllPriorEdits = useCallback(async () => {
    await Promise.all([mutatePriorEdits(), mutateCascadePriorEdits()])
  }, [mutateCascadePriorEdits, mutatePriorEdits])

  const isQueryEnabled = enabled && loadOptions !== undefined
  const isLoading =
    isQueryEnabled &&
    (isLoadingDocuments ||
      (priorEditLoadOptions !== undefined && isLoadingPriorEdits) ||
      isCascadeLoading)

  const arePriorEditsReady =
    (priorEditLoadOptions === undefined || rawPriorEdits !== undefined) &&
    areCascadePriorEditsReady

  return {
    members: displayMembers,
    priorEditsByDocumentEntryId,
    pagesByBqDocumentId,
    priorPageEditsByBqDocumentId,
    mutatePriorEdits: mutateAllPriorEdits,
    arePriorEditsReady,
    isLoading,
    familySize: displayMembers.length,
    hasRevisionFamily: displayMembers.length > 1,
  }
}
